import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { SoulId } from "../id"
import { DocumentSnapshotLike } from "../firestore"

/**
 * 魂（Soul）ドキュメントスキーマ。
 *
 * コレクション: `souls/{soulId}`
 * 魂はアバターの挙動パラメータセットを表す。VC連携VRM に紐付けて使用する。
 * クライアントは Firestore から直接読み取り、管理ツールから登録・編集する。
 */
export const SoulDocumentMeta = z.object({
  name: z.string().describe("魂の表示名。例: 'neutral', 'lively_grace'"),
  description: z.string().optional().describe("魂の説明文。"),
  priority: z.number().int().describe("表示順。数値が大きいほど先に表示する。"),
  enabled: z.boolean().describe("有効フラグ。false の場合、新規選択不可（既に設定済みのユーザーには影響しない）。"),
})

export type SoulDocumentMeta = z.infer<typeof SoulDocumentMeta>

export const SoulDocumentBody = z.intersection(
  SoulDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SoulDocumentBody = z.infer<typeof SoulDocumentBody>

export type SoulDocument = SoulDocumentBody & {
  id: SoulId
}

/**
 * 魂 ID のバリデーション。
 * 小文字英数字・ハイフン・アンダースコアのみ許可。先頭は英小文字。
 * Firestore ドキュメントID の制約（1500バイト、'/' 禁止等）よりも厳しく制限する。
 */
const SoulIdentifier = z.string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_-]*$/, "id は英小文字で始まり、英小文字・数字・ハイフン・アンダースコアのみ使用できます。")
  .describe("魂の識別子。Firestore ドキュメントIDとして使用される。")

export const CreateSoulRequest = z.object({
  id: SoulIdentifier,
  name: z.string().min(1).describe("魂の識別名。例: 'neutral', 'lively_grace'"),
  description: z.string().optional().describe("魂の説明文。"),
  priority: z.coerce.number().int().describe("表示順。数値が大きいほど先に表示する。"),
  enabled: z.boolean().describe("有効フラグ。"),
})

export type CreateSoulRequest = z.infer<typeof CreateSoulRequest>

/**
 * `PUT /manage/souls/{id}` のリクエストボディ。
 * 全フィールド任意（部分更新）。
 */
export const UpdateSoulRequest = z.object({
  name: z.string().min(1).optional().describe("魂の識別名。"),
  description: z.string().optional().describe("魂の説明文。"),
  priority: z.coerce.number().int().optional().describe("表示順。"),
  enabled: z.boolean().optional().describe("有効フラグ。"),
})

export type UpdateSoulRequest = z.infer<typeof UpdateSoulRequest>

export class SoulDocuments {

  static toDocumentData(doc: SoulDocument): object {
    return {
      ...doc,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SoulDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SoulId(doc.id),
      name: data["name"],
      description: data["description"],
      priority: data["priority"],
      enabled: data["enabled"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
